import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useSourceHistory } from '@/hooks/use-source-history'
import { useSources } from '@/hooks/use-sources'

export default function SourceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { sources, isLoading: sourcesLoading } = useSources()
  const { history, isLoading: historyLoading, error } = useSourceHistory(id)

  const source = sources.find(s => s.id === id)
  const isLoading = sourcesLoading || historyLoading

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getHistoryStatusBadge = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <Badge className="bg-green-300 text-secondary-foreground">Sucesso</Badge>
      case 'FAILED':
        return <Badge className="bg-red-100 text-secondary-foreground">Falhou</Badge>
      default:
        return <Badge className="bg-gray-100 text-secondary-foreground">Desconhecido</Badge>
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-600" />
          <p className="text-gray-600">Carregando detalhes da fonte...</p>
        </div>
      </div>
    )
  }

  if (!source) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Fonte não encontrada</h2>
          <p className="text-gray-600 mb-4">A fonte solicitada não foi encontrada.</p>
          <Button onClick={() => navigate('/app/settings/sources')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Fontes
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-8">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">{source.name}</h2>
          <p className="text-muted-foreground">Visualize e inspecione os dados sincronizados.</p>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Data/Hora da Sincronização</TableHead>
            <TableHead>Mensagem</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {historyLoading ? (
            <TableRow>
              <TableCell colSpan={3} className="p-8 text-center">
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Carregando histórico...</span>
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={3} className="p-8 text-center text-red-500">
                Erro ao carregar histórico: {error}
              </TableCell>
            </TableRow>
          ) : history.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="p-8 text-center text-gray-500">
                Nenhum histórico encontrado
              </TableCell>
            </TableRow>
          ) : (
            history.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="p-4">
                  {getHistoryStatusBadge(item.status)}
                </TableCell>
                <TableCell className="p-4 text-gray-600">
                  {formatDate(item.created_at)}
                </TableCell>
                <TableCell className="p-4 text-gray-600">
                  {item.error_message || '-'}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
